import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { BehaviorSubject, EMPTY, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class SearchService {
  apiUrl: string = '';
  apiKey: string = '';
  searchUrl: string = '';
  private _sharedModalValue = new BehaviorSubject<any>(null);

  set sharedModalWithSource(value: any) {
    this._sharedModalValue.next(value);
  }

  lastRequest!: SearchRequest;

  constructor(private http: HttpClient, @Inject(OKTA_AUTH) private auth: OktaAuth, @Inject('CONFIG_SETTINGS') config: any) {
    const configSettings = config['general'];
    this.apiUrl = configSettings['adminApiUrl'];
    this.apiKey = configSettings['apiKey'];
    this.searchUrl = this.apiUrl + 'search';

  }


  private getSingleItem<T>(url: string): Observable<T> {
    const token = this.getToken();
    //const url = this.apiUrl + this.getEndpoint;
    if (token != undefined) {
      return this.http.get<T>(url, this.getHttpOptions(token));
    }
    else {
      return EMPTY;
    }
  }

  private getMultipleItems<T>(url: string): Observable<T[]> {
    const token = this.getToken();
    //const url = this.apiUrl + this.getEndpoint;
    if (token != undefined) {
      return this.http.get<T[]>(url, this.getHttpOptions(token));
    }
    else {
      return EMPTY;
    }
  }

  private postSingleItem<T>(url: string, content: any): Observable<T> {
    const token = this.getToken();
    //const url = this.apiUrl + this.getEndpoint;
    if (token != undefined) {
      return this.http.post<T>(url, content, this.getHttpOptions(token));
    }
    else {
      return EMPTY;
    }
  }

  private getToken(): string | undefined {
    return this.auth.getAccessToken();
  }

  private getHttpOptions(token: string): object {

    if (this.apiKey.length > 0) {
      return {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token,
          'Ocp-Apim-Subscription-Key': this.apiKey
        })
      };
    }
    else {
      return {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token
        })
      };
    }
  }

  runNewSearch(term: string, facets: ISearchFacetItemQueryExpr[]): Promise<ISearchResult> {

    const request = new SearchRequest();
    request.term = term;
    request.page = 1;
    request.resultPages = null;
    request.facets = facets;
    request.token = this.getToken();

    return this.runNewSearchFromRequest(request);
  }

  runNewSearchFromRequest(request: SearchRequest): Promise<ISearchResult> {

    const lastResult = this.postSingleItem<ISearchResult>(this.searchUrl, request).toPromise()
      .then(res => {
        res.facets.forEach((facet) => {
          if (facet.entries.some(entry => entry.checked)) {
            facet.expanded = true;
          }
        });
        request.resultPages = res.resultPages;
        this.lastRequest = request;
        return res;
      });

    return lastResult;
  }

  getSearchPage(page: number): Promise<ISearchResult> {

    this.lastRequest.page = page;
    // set current pages to false;
    this.lastRequest.resultPages?.pages.forEach(p => {
      if (p.pageNumber == page - 1) {
        p.currentPage = true;
      }
      else {
        p.currentPage = false;
      }
    });

    const lastResult = this.postSingleItem<ISearchResult>(this.searchUrl, this.lastRequest).toPromise()
      .then(res => {
        this.lastRequest.resultPages = res.resultPages;
        return res;
      });
    this.lastRequest.page = page;

    return lastResult;
  }

}

export class SearchRequest {
  term: string = "";
  page: number = 1;
  resultPages: ISearchResultPages | null = null;
  facets?: ISearchFacetItemQueryExpr[] | [] = [];
  sort: string = "";
  sortDirection: string = "";
  token?: string = "";
  constructor() { }
}

export interface ISavedSearch {
  id: string;
  name:string,
  searchRequest: SearchRequest;
  userEmail: string;
  timestamp: Date;
}

export interface ISearchResult {
  name: string;
  estimatedCount: number;
  pageNumber: number;
  pageSize: number;
  elapsedTime: number;
  results: ISearchItem[]
  resultPages: ISearchResultPages
  facets: ISearchFacet[];
}

export interface ISearchItem {
  id: string;
  title: string;
  content: string;
  extension: string;
  size: string;
  sizeNumeric: number;
  date: Date;
  url: string;
  folder: string;
  source: string;
  thumbnail: string;
}

export interface ISearchResultPages {
  queryEngines: ISearchQueryEngine[];
  pages: ISearchPage[];
  pageSize: number;
  pageCount: number;
}

export interface ISearchPage {
  starts: number[];
  counts: number[];
  pageNumber: number;
  order: ISearchOrder;
  currentPage: boolean | undefined;
}

export interface ISearchOrder {
  num: number;
}

export interface ISearchQueryEngine {
  serviceId: string;
  queryEngineId: string;
  digest: string;
}

export interface ISearchFacet {
  id: string;
  name: string;
  displayName: string;
  entries: ISearchFacetEntry[];
  expanded: boolean;
}

export interface ISearchFacetEntry {
  html: string;
  description: string;
  count: number;
  queryExpr: ISearchFacetItemQueryExpr;
  checked: boolean;
}

export interface ISearchFacetItemQueryExpr {
  label: string;
  regex: string;
  description: string;
  id: string;
  value: ISearchValue;
}

export interface ISearchValue {
  num: number;
  str: string;
}
